import { inject, Injectable } from '@angular/core';
import { isSupported, getMessaging, onMessage, getToken } from 'firebase/messaging';
import { TranslateService } from '@ngx-translate/core';

// Services
import { SystemNotificationsService } from './system-notifications.service';

// Constants
import { LOCAL_STORAGE_KEYS } from '@constants/storage.constants';

// Configs
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private systemNotificationsService = inject(SystemNotificationsService);
  private translateService = inject(TranslateService);

  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      // Check whether notification permissions have support desktop notification;
      this.systemNotificationsService.openErrorSnackBar('Desktop notifications not supported');
      return false;
    }

    if (Notification.permission === 'granted') {
      // Check whether notification permissions have already been granted;
      await this.generateFirebaseToken();
      return true;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        await this.generateFirebaseToken();
        return true;
      }
      this.systemNotificationsService.openErrorSnackBar(
        'Notification permission denied by user. Please enable permissions',
      );
      return false;
    } catch (error) {
      console.error('Notification.requestPermission error', error);
      this.systemNotificationsService.openErrorSnackBar(
        'Error while requesting notification permission',
      );
      return false;
    }
  }

  public async generateFirebaseToken(): Promise<string | null> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async resolve => {
      try {
        const supported = await isSupported();
        if (!supported) {
          this.systemNotificationsService.openErrorSnackBar(
            'Your browser does not support Firebase messaging. Please try using a different browser or update your current browser to the latest version for full functionality.',
          );
          resolve(null);
        }

        const messaging = getMessaging();

        const currentToken = await getToken(messaging, { vapidKey: environment.firebase.vapidKey });
        if (!currentToken) {
          console.error('No registration token available. Request permission to generate one.');
          resolve(null);
        }

        localStorage.setItem(LOCAL_STORAGE_KEYS.DEVICE_UDID, currentToken);
        this.listenNotifications();

        resolve(currentToken);
      } catch (error: unknown) {
        console.error('Request notifications permission', error);

        if (
          error instanceof Error &&
          error?.message.includes("Failed to execute 'subscribe' on 'PushManager'")
        ) {
          window.location.reload();
        }

        resolve(null);
      }
    });
  }

  public async listenNotifications(): Promise<void> {
    const messaging = getMessaging();

    onMessage(messaging, async payload => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (!registrations.length) return;

        const payloadItems = payload.data?.['items'];
        if (!payloadItems) return;
        const parsedData = JSON.parse(payloadItems);

        registrations[0]?.showNotification(parsedData.data?.['title'] ?? 'Help me Fix', {
          body: parsedData?.data?.['body'],
          icon: './assets/firebase-logo.svg',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          actions: [
            {
              action: 'openApp',
              title: this.translateService.instant('common.notification-open-action'),
            },
          ],
          data: parsedData,
        });
      } catch (error) {
        console.error('Listen Notifications onMessage error', error);
      }
    });
  }
}
